// src/components/PDF/PDFTools.js
import React, { useState } from 'react';
import { 
  Merge, 
  Split, 
  Image, 
  Plus, 
  RotateCw,
  Minimize,
  Lock,
  Loader2,
  FileInput,
  Pencil,
  PanelsTopLeft
} from 'lucide-react';
import MergePDF from './MergePDF';
import SplitPDF from './SplitPDF';
import OrganizePDF from './OrganizePDF';
import ConvertPDF from './ConvertPDF';
import CompressPDF from './CompressPDF';
import AddMediaPDF from './AddMediaPDF';
import ProtectPDF from './ProtectPDF';
import FillablePDF from './FillablePDF';
import PDFEditor from './PDFEditor';
import CanvasEditor from './CanvasEditor';

const DocTools = () => {
  const [workspace, setWorkspace] = useState('pdf');
  const [activeTab, setActiveTab] = useState('merge');
  const [isLoading, setIsLoading] = useState(false);

  const tabs = [
    { id: 'edit', label: 'Edit PDF', icon: Pencil, component: PDFEditor },
    { id: 'merge', label: 'Merge PDF', icon: Merge, component: MergePDF },
    { id: 'split', label: 'Split PDF', icon: Split, component: SplitPDF },
    { id: 'organize', label: 'Organize', icon: RotateCw, component: OrganizePDF },
    { id: 'convert', label: 'To JPEG', icon: Image, component: ConvertPDF },
    { id: 'compress', label: 'Compress', icon: Minimize, component: CompressPDF },
    { id: 'media', label: 'Add Media', icon: Plus, component: AddMediaPDF },
    { id: 'protect', label: 'Protect', icon: Lock, component: ProtectPDF },
    { id: 'fillable', label: 'Fillable Form', icon: FileInput, component: FillablePDF },
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  return (
    <div className={`mx-auto ${workspace === 'canvas' ? 'max-w-7xl' : 'max-w-5xl'}`}>
      {/* Workspace toggle */}
      <div className="mb-4 flex justify-center">
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5 shadow-sm" role="tablist" aria-label="Document tool workspace">
          <button
            type="button"
            role="tab"
            aria-selected={workspace === 'pdf'}
            onClick={() => setWorkspace('pdf')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${workspace === 'pdf' ? 'bg-[#232751] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <FileInput className="h-3.5 w-3.5" /> PDF
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={workspace === 'canvas'}
            onClick={() => setWorkspace('canvas')}
            className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${workspace === 'canvas' ? 'bg-[#232751] text-white shadow-sm' : 'text-slate-600 hover:bg-slate-50'}`}
          >
            <PanelsTopLeft className="h-3.5 w-3.5" /> Canvas
          </button>
        </div>
      </div>

      {workspace === 'pdf' ? (
        <>
          {/* Tab Navigation */}
          <div className="flex justify-center mb-8 overflow-x-auto pb-2">
            <div className="inline-flex p-1 bg-white rounded-xl shadow-sm border gap-0.5">
              {tabs.map(tab => {
                const IconComponent = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`
                      flex items-center px-3 lg:px-4 py-2.5 lg:py-3 rounded-lg text-xs lg:text-sm font-medium transition-all duration-200 whitespace-nowrap
                      ${activeTab === tab.id 
                        ? 'bg-[#232751] text-white shadow-md' 
                        : 'text-gray-600 hover:bg-gray-50'
                      }
                    `}
                  >
                    <IconComponent className="w-4 h-4 lg:w-5 lg:h-5 lg:mr-2" />
                    <span className="hidden lg:inline">{tab.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Mobile Tab Label */}
          <div className="lg:hidden text-center mb-4">
            <span className="text-sm font-medium text-[#232751]">
              {tabs.find(t => t.id === activeTab)?.label}
            </span>
          </div>

          {/* Content */}
          <div className="bg-white/80 backdrop-blur-sm shadow-xl rounded-2xl border-0 p-4 md:p-8">
            {isLoading && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg p-6 flex items-center space-x-3">
                  <Loader2 className="w-6 h-6 animate-spin text-[#232751]" />
                  <span className="text-gray-700 font-medium">Processing...</span>
                </div>
              </div>
            )}
            
            {ActiveComponent && (
              <ActiveComponent 
                isLoading={isLoading} 
                setIsLoading={setIsLoading} 
              />
            )}
          </div>
        </>
      ) : (
        <CanvasEditor />
      )}
    </div>
  );
};

export default DocTools;
